import Papa from 'papaparse';

import { MAX_CSV_VOICEBOX_LINES } from 'src/constants/limits';
import { CompetencyQuestion } from 'src/constants/voicebox';
import {
  Mapping,
  MappingIdentifierType,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { isResource } from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { Project, ProjectFileUpload } from 'src/store/state/projects';
import { formatSeparator } from 'src/utils/csv/formatSeparator';
import { getValidMappingsByResource } from 'src/utils/export/validateMappingsByResource';
import { stringifyModelConceptAttributes } from 'src/utils/stringify/modelConceptAttributes';
import { stringifyModelConceptClasses } from 'src/utils/stringify/modelConceptClasses';
import { stringifyModelConceptRelationships } from 'src/utils/stringify/modelConceptRelationships';
import {
  GenerateDataModelRequestParameters,
  GenerateDataRequestParameters,
  GenerateMappingsRequestParameters,
  GenerateQueriesRequestParameters,
  GenerateQuestionsRequestParameters,
  GenerateScoresRequestParameters,
  GenerateSummaryRequestParameters,
  GenesisDataSource,
  GenesisMapping,
} from 'src/utils/voicebox/hooks/types';

export const getAllResourceIds = (project: Project): Set<string> =>
  project.diagram.model.nodeDataArray.reduce((acc, data) => {
    if (isResource(data)) {
      acc.add(data.value);
    }
    return acc;
  }, new Set<string>());

export const getValidResourceIds = (validMappings: {
  classMappingsByResource: Record<string, Mapping[]>;
  relationshipMappingsByResource: Record<string, RelationshipMapping[]>;
}) =>
  new Set([
    ...Object.keys(validMappings.classMappingsByResource),
    ...Object.keys(validMappings.relationshipMappingsByResource),
  ]);

export const getLimitedCsvData = (resource: ProjectFileUpload): string => {
  const hasHeader = resource.options.header !== false;

  const parseResult = Papa.parse(resource.contents, {
    delimiter: formatSeparator(resource.options.separator),
    quoteChar: resource.options.quote,
    escapeChar: resource.options.escape,
    header: hasHeader,
    skipEmptyLines: resource.options.skipEmpty,
    preview: MAX_CSV_VOICEBOX_LINES,
  });

  let result = Papa.unparse(parseResult.data, {
    delimiter: formatSeparator(resource.options.separator),
    quoteChar: resource.options.quote,
    escapeChar: resource.options.escape,
    header: hasHeader,
  });

  if (!hasHeader && resource.fields && resource.fields.length > 0) {
    const headers = resource.fields
      .map((field) => field.label || field.value)
      .join(formatSeparator(resource.options.separator));
    result = `${headers}\n${result}`;
  }

  return result;
};

export const buildModelParameter = (
  project: Project,
  classIris?: string[]
): string => {
  const classIrisSet = new Set(classIris);
  const classes = stringifyModelConceptClasses({
    filter: classIris ? ({ value }) => classIrisSet.has(value) : undefined,
    model: project.diagram.model,
    targetModelId: project.targetModelId,
    includeMetadata: false,
  });
  const attributes = stringifyModelConceptAttributes({
    classIris,
    filter: classIris
      ? ({ domains, ranges }) =>
          Object.keys(domains).some((domain) => classIrisSet.has(domain)) ||
          Object.keys(ranges).some((range) => classIrisSet.has(range))
      : undefined,
    model: project.diagram.model,
    projectPropertyType: project.propertyType,
    targetModelId: project.targetModelId,
  });
  const relationships = stringifyModelConceptRelationships({
    classIris,
    filter: classIris
      ? ([_, { domains, ranges }]) =>
          Array.from(domains).some((domain) => classIrisSet.has(domain)) ||
          Array.from(ranges).some((range) => classIrisSet.has(range))
      : undefined,

    model: project.diagram.model,
    projectPropertyType: project.propertyType,
    targetModelId: project.targetModelId,
  });

  return [classes, attributes, relationships].filter((str) => str).join('\n\n');
};

export const buildDataParameter = (
  project: Project,
  resourceIds: Set<string>
): Record<string, GenesisDataSource> => {
  const dataSourcesById: Record<string, GenesisDataSource> = {};

  Object.values(project.fileUploadsById).forEach((fileUpload) => {
    if (resourceIds.has(fileUpload.id)) {
      dataSourcesById[fileUpload.id] = {
        id: fileUpload.id,
        type: 'csv',
        name: fileUpload.name,
        quote: fileUpload.options.quote,
        separator: fileUpload.options.separator,
        data: getLimitedCsvData(fileUpload),
      };
    }
  });

  Object.values(project.virtualGraphsById).forEach((virtualGraph) => {
    if (resourceIds.has(virtualGraph.id)) {
      if (typeof virtualGraph.from === 'string') {
        dataSourcesById[virtualGraph.id] = {
          id: virtualGraph.id,
          type: 'sql_query',
          name: virtualGraph.name,
          query: virtualGraph.from,
          data_source: virtualGraph.dataSourceId,
        };
      } else {
        dataSourcesById[virtualGraph.id] = {
          id: virtualGraph.id,
          type: 'sql_table',
          name: [
            virtualGraph.from.catalog,
            virtualGraph.from.schema,
            virtualGraph.from.name,
          ]
            .filter(Boolean)
            .join('.'),
          data_source: virtualGraph.dataSourceId,
        };
      }
    }
  });

  return dataSourcesById;
};

export const buildMappingsParameter = (
  validMappings: {
    classMappingsByResource: Record<string, Mapping[]>;
    relationshipMappingsByResource: Record<string, RelationshipMapping[]>;
  },
  dataSourcesById: Record<string, GenesisDataSource>
): GenesisMapping[] => {
  const classMappingsByKey = Object.entries(
    validMappings.classMappingsByResource
  ).reduce<
    Record<
      string,
      Mapping & {
        relationshipMappings: RelationshipMapping[];
        resourceId: string;
      }
    >
  >((acc, [resourceId, mappings]) => {
    mappings.forEach((mapping) => {
      acc[mapping.key] = { ...mapping, relationshipMappings: [], resourceId };
    });
    return acc;
  }, {});

  Object.values(validMappings.relationshipMappingsByResource).forEach(
    (relationshipMappings) => {
      relationshipMappings.forEach((relationshipMapping) => {
        if (
          relationshipMapping.from &&
          classMappingsByKey[relationshipMapping.from]
        ) {
          classMappingsByKey[
            relationshipMapping.from
          ].relationshipMappings.push(relationshipMapping);
        }

        if (
          relationshipMapping.to &&
          classMappingsByKey[relationshipMapping.to]
        ) {
          classMappingsByKey[relationshipMapping.to].relationshipMappings.push(
            relationshipMapping
          );
        }
      });
    }
  );

  const mappings: GenesisMapping[] = [];
  Object.values(classMappingsByKey).forEach((mapping) => {
    if (!mapping.to || !dataSourcesById[mapping.resourceId]) {
      return;
    }

    const columns: Record<string, string[]> = {};
    Object.values(mapping.fields).forEach((field) => {
      field.targets.forEach((attributeIri) => {
        columns[attributeIri] = [field.value];
      });
    });
    mapping.relationshipMappings.forEach((relMapping) => {
      const fields = (
        relMapping.fromPrimaryIdentifier || relMapping.toPrimaryIdentifier
      )?.parts
        .filter((part) => part.type === MappingIdentifierType.FIELD)
        .map((part) => part.value);
      if (fields?.length) {
        columns[relMapping.relationshipIri] = fields;
      }
    });

    mappings.push({
      table_id: dataSourcesById[mapping.resourceId].id,
      table_name: dataSourcesById[mapping.resourceId].name,
      concept: getModelConceptClassValue(mapping.to),
      id: mapping.primaryIdentifier.parts
        .filter((part) => part.type === MappingIdentifierType.FIELD)
        .map((part) => part.value),
      label: mapping.label ? [mapping.label] : [],
      columns,
    });
  });

  return mappings;
};

export const buildGenerateDataModelParameters = ({
  project,
}: {
  project: Project;
}): GenerateDataModelRequestParameters => {
  const { competencyQuestions } = project.diagram.model.modelData;
  const questions = competencyQuestions.map((question) => ({
    question: question.question,
  }));

  return {
    project: project.name,
    description: project.description || '',
    questions,
    namespace: project.dataNamespace,
  };
};

export const buildGenerateDataParameters = ({
  project,
  classIris,
}: {
  project: Project;
  classIris?: string[];
}): GenerateDataRequestParameters => {
  const { competencyQuestions } = project.diagram.model.modelData;
  const questions = competencyQuestions.map((question) => ({
    question: question.question,
  }));

  const modelContent = buildModelParameter(project, classIris);

  return {
    project: project.name,
    description: project.description || '',
    namespace: project.dataNamespace,
    model: modelContent,
    questions,
  };
};

export const buildGenerateMappingsParameters = ({
  project,
  projectId,
  resourceIds,
  classIris,
}: {
  project: Project;
  projectId: string;
  resourceIds: Set<string>;
  classIris: string[];
}): GenerateMappingsRequestParameters => {
  const dataSourcesById = buildDataParameter(project, resourceIds);

  const { competencyQuestions } = project.diagram.model.modelData;
  const questions = competencyQuestions.map((question) => ({
    question: question.question,
  }));

  const modelContent = buildModelParameter(project, classIris);

  return {
    project: projectId,
    description: project.description || '',
    questions,
    namespace: project.dataNamespace,
    data: Object.values(dataSourcesById),
    model: modelContent,
  };
};

export const buildGenerateQueriesParameters = (
  project: Project,
  questions: CompetencyQuestion[]
): GenerateQueriesRequestParameters => {
  const validMappings = getValidMappingsByResource(project.diagram.model);
  const resourceIds = getValidResourceIds(validMappings);

  const dataSourcesById = buildDataParameter(project, resourceIds);

  return {
    project: project.name,
    description: project.description || '',
    questions: questions.map((question) => ({
      id: question.id,
      question: question.question,
    })),
    model: buildModelParameter(project),
    data: Object.values(dataSourcesById),
    mappings: buildMappingsParameter(validMappings, dataSourcesById),
  };
};

export const buildGenerateQuestionsParameters = (
  name: string,
  description: string
): GenerateQuestionsRequestParameters => {
  return {
    project: name,
    description: description || '',
  };
};

export const buildGenerateScoresParameters = (
  project: Project,
  questions: CompetencyQuestion[]
): GenerateScoresRequestParameters => {
  const validMappings = getValidMappingsByResource(project.diagram.model);
  const resourceIds = getValidResourceIds(validMappings);

  const dataSourcesById = buildDataParameter(project, resourceIds);

  return {
    project: project.name,
    description: project.description || '',
    questions: questions.map((question) => ({
      id: question.id,
      question: question.question,
    })),
    model: buildModelParameter(project),
    data: Object.values(dataSourcesById),
    mappings: buildMappingsParameter(validMappings, dataSourcesById),
  };
};

export const buildGenerateSummaryParameters = (
  project: Project,
  questions: CompetencyQuestion[]
): GenerateSummaryRequestParameters => {
  const validMappings = getValidMappingsByResource(project.diagram.model);
  const resourceIds = getAllResourceIds(project);

  const dataSourcesById = buildDataParameter(project, resourceIds);

  return {
    project: project.name,
    description: project.description || '',
    questions: questions
      .filter(
        (
          question
        ): question is CompetencyQuestion & {
          evaluation: NonNullable<CompetencyQuestion['evaluation']>;
        } => Boolean(question.evaluation)
      )
      .map((question) => ({
        id: question.id,
        question: question.question,
        score: question.evaluation.score,
      })),
    model: buildModelParameter(project),
    data: Object.values(dataSourcesById),
    mappings: buildMappingsParameter(validMappings, dataSourcesById),
  };
};
